import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { propertyData } from '../mockData';
import { locationDetails } from '../mockData_extended';
import { MapPin, Clock, Navigation } from 'lucide-react';

// Property coordinates
const PROPERTY_LAT = 39.17873737692183;
const PROPERTY_LNG = 23.619745031814166;

const Location = () => {
  const { language } = useLanguage();
  const content = (propertyData[language] || propertyData.en).location;
  const distances = locationDetails[language] || locationDetails.en;

  // Generate Google Maps embed URL with exact coordinates and marker
  const mapEmbedUrl = `https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3000!2d${PROPERTY_LNG}!3d${PROPERTY_LAT}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMznCsDEwJzQzLjUiTiAyM8KwMzcnMTEuMSJF!5e0!3m2!1sen!2sgr!4v1705000000000`;

  // Direct link to Google Maps for the property location
  const googleMapsDirectLink = `https://www.google.com/maps?q=${PROPERTY_LAT},${PROPERTY_LNG}`;

  return (
    <section id="location" className="py-16 sm:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-light text-slate-800 mb-6 sm:mb-8 text-center">
          {content.title}
        </h2>
        <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-4xl mx-auto text-center mb-8 sm:mb-12 px-2">
          {content.description}
        </p>

        {/* Distances Grid */}
        <div className="max-w-4xl mx-auto mb-8 sm:mb-12">
          <h3 className="text-xl sm:text-2xl font-semibold text-slate-800 mb-4 sm:mb-6 text-center flex items-center justify-center gap-2">
            <MapPin className="w-5 h-5 sm:w-6 sm:h-6 text-[#d4845f]" />
            {distances.title}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {distances.distances.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 sm:p-4 bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-[#d4845f] flex-shrink-0" />
                  <span className="text-sm sm:text-base text-slate-700 font-medium">{item.label}</span>
                </div>
                <span className="text-sm sm:text-base text-[#d4845f] font-semibold whitespace-nowrap ml-2">{item.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Map */}
        <div className="max-w-5xl mx-auto">
          <div className="rounded-xl overflow-hidden shadow-xl">
            <iframe
              title="Property Location - Skopelos"
              src={mapEmbedUrl}
              width="100%"
              height="350"
              className="sm:h-[500px]"
              style={{ border: 0, minHeight: '300px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
          
          {/* Open in Google Maps button */}
          <div className="flex justify-center mt-4 sm:mt-6 px-4 sm:px-0">
            <a
              href={googleMapsDirectLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 bg-[#d4845f] hover:bg-[#c17552] text-white rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 text-sm sm:text-base"
              data-testid="open-google-maps-button"
            >
              <Navigation className="w-4 h-4 sm:w-5 sm:h-5" />
              {language === 'en' ? 'Open in Google Maps' : 
               language === 'gr' ? 'Άνοιγμα στο Google Maps' :
               language === 'de' ? 'In Google Maps öffnen' :
               language === 'fr' ? 'Ouvrir dans Google Maps' :
               language === 'zh' ? '在谷歌地图中打开' :
               language === 'ru' ? 'Открыть в Google Картах' :
               language === 'ar' ? 'فتح في خرائط جوجل' :
               language === 'he' ? 'פתח ב-Google Maps' :
               'Open in Google Maps'}
            </a>
          </div>
          
          {/* Coordinates display */}
          <p className="text-center text-xs sm:text-sm text-slate-500 mt-3 sm:mt-4">
            <MapPin className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
            {PROPERTY_LAT.toFixed(6)}°N, {PROPERTY_LNG.toFixed(6)}°E
          </p>
        </div>
      </div>
    </section>
  );
};

export default Location;